import React, { useMemo, useState, useCallback } from 'react';
import { Box, Typography, Button, Popover, Checkbox, FormControlLabel, MenuItem, Menu } from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import MainCard from '../../../components/cards/MainCard';

// Consistent left-aligned "View" affordance used on every drillable table
// row. Replaces whole-row click so the click target is explicit and the
// pattern matches the rest of the portal.
function RowViewButton({ onClick }) {
    return (
        <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={(e) => { e.stopPropagation(); onClick(); }}
            sx={{
                borderRadius: '4px', borderColor: 'divider', color: 'text.secondary',
                fontSize: 11, fontWeight: 600, py: '2px', px: '10px', minWidth: 0, textTransform: 'none',
                '&:hover': { borderColor: 'secondary.main', color: 'secondary.main', bgcolor: 'transparent' },
            }}
        >
            View
        </Button>
    );
}

function escapeCsv(value) {
    if (value == null) return '';
    const s = String(value);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadBlob(content, mime, filename) {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
}

function rowsToCsv(rows, columns) {
    const header = columns.map((c) => escapeCsv(c.label)).join(',');
    const body = rows.map((r) => columns.map((c) => {
        const v = c.accessor(r);
        const fmt = c.format ? c.format(v, r) : v;
        return escapeCsv(typeof fmt === 'string' || typeof fmt === 'number' ? fmt : v);
    }).join(',')).join('\n');
    return `${header}\n${body}`;
}

function rowsToJson(rows, columns) {
    return JSON.stringify(rows.map((r) => Object.fromEntries(columns.map((c) => [c.id, c.accessor(r)]))), null, 2);
}

function ExportMenu({ rows, columns, title }) {
    const [anchor, setAnchor] = useState(null);
    const base = (title || 'export').replace(/\s+/g, '-').toLowerCase();
    const exportAs = (kind) => {
        if (kind === 'csv')  downloadBlob(rowsToCsv(rows, columns),  'text/csv;charset=utf-8',     `${base}.csv`);
        if (kind === 'json') downloadBlob(rowsToJson(rows, columns), 'application/json',           `${base}.json`);
        setAnchor(null);
    };
    return (
        <>
            <Button
                size="small" variant="outlined"
                startIcon={<FileDownloadIcon sx={{ fontSize: 14 }} />}
                onClick={(e) => setAnchor(e.currentTarget)}
                disabled={rows.length === 0}
                sx={{
                    borderRadius: '4px', borderColor: 'divider', color: 'text.secondary',
                    fontSize: 11, py: '3px', px: '10px', textTransform: 'none',
                    '&:hover': { borderColor: 'secondary.main', color: 'secondary.main' },
                }}
            >
                Export ({rows.length})
            </Button>
            <Menu
                anchorEl={anchor}
                open={!!anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { mt: 0.5, border: '1px solid', borderColor: 'divider', boxShadow: '0 4px 20px rgba(0,0,0,0.10)', borderRadius: '8px', minWidth: 160 } }}
            >
                <Box sx={{ px: 1.5, py: 0.5, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled' }}>
                    This table
                </Box>
                <MenuItem onClick={() => exportAs('csv')}  sx={{ fontSize: 12 }}>CSV</MenuItem>
                <MenuItem onClick={() => exportAs('json')} sx={{ fontSize: 12 }}>JSON</MenuItem>
                <Box sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 0.5, pt: 0.5 }}>
                    <Box sx={{ px: 1.5, py: 0.5, fontSize: 10, color: 'text.disabled' }}>
                        Excel and PDF live in the Export Builder.
                    </Box>
                </Box>
            </Menu>
        </>
    );
}

// Shared table primitive used by SendHistory, CourseSendLog, HighRiskUsers,
// OutstandingUsers and UsersPage. Implements toolbar, column picker,
// sortable headers, pagination, sticky header and CSV export.
//
// Calculations: client-side sort and slice. Sort key is a column accessor
// function or property name; ascending / descending derived from local state.
// Pagination is windowed (rows.slice(offset, offset + pageSize)).
//
// Postgres mapping: client-side sort/page is OK for <2k rows; beyond
// that the consuming endpoints should accept (sortBy, direction, limit,
// offset) and the table should switch to server-driven mode.
//
// Integration hook: each consumer brings its own RTK Query slice; this
// component stays presentational. No internal API surface.
// Shared table primitive. Every other tabular page in the dashboard is
// expected to use this so the toolbar, column picker, sortable headers,
// pagination and footer behave the same everywhere. The reference for the
// look-and-feel is the Users page; this file just hoists those patterns
// into a reusable shell.
//
// Column shape:
//   { id, label, accessor: (row) => value,
//     sortable?: boolean, type?: 'string' | 'number',
//     align?: 'left' | 'right',
//     always?: boolean,    // always shown, can't be hidden by the picker
//     defaultOn?: boolean, // included in the default column choice
//     format?: (value, row) => string,
//     render?: (row) => ReactNode, // full custom cell, bypasses format
//     width?: string | number,
//   }

const PAGE_SIZES = [25, 50, 100, 200];

function compareValues(a, b, type) {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    if (type === 'number') return a - b;
    return String(a).localeCompare(String(b));
}

function loadCols(storageKey, fallback) {
    if (!storageKey) return fallback;
    try {
        const stored = JSON.parse(localStorage.getItem(storageKey));
        if (Array.isArray(stored) && stored.length > 0) return stored;
    } catch { /* fall through */ }
    return fallback;
}

function ColumnPicker({ columns, active, onChange }) {
    const [anchor, setAnchor] = useState(null);
    const toggle = (id) => {
        const col = columns.find((c) => c.id === id);
        if (col?.always) return;
        const next = active.includes(id) ? active.filter((x) => x !== id) : [...active, id];
        onChange(next);
    };
    return (
        <>
            <Button
                size="small" variant="outlined"
                startIcon={<ViewColumnIcon sx={{ fontSize: 14 }} />}
                onClick={(e) => setAnchor(e.currentTarget)}
                sx={{
                    borderRadius: '4px', borderColor: 'divider', color: 'text.secondary',
                    fontSize: 11, py: '3px', px: '10px', textTransform: 'none',
                    '&:hover': { borderColor: 'secondary.main', color: 'secondary.main' },
                }}
            >
                Columns ({active.length})
            </Button>
            <Popover
                open={!!anchor}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { p: 1, mt: 0.5, border: '1px solid', borderColor: 'divider', boxShadow: '0 4px 20px rgba(0,0,0,0.10)', borderRadius: '8px', minWidth: 200 } }}
            >
                <Box sx={{ px: 1, py: 0.5, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled' }}>
                    Columns
                </Box>
                {columns.map((c) => (
                    <FormControlLabel
                        key={c.id}
                        control={
                            <Checkbox
                                checked={active.includes(c.id)}
                                disabled={c.always}
                                onChange={() => toggle(c.id)}
                                size="small"
                                sx={{ p: 0.5, color: 'text.disabled', '&.Mui-checked': { color: 'secondary.main' } }}
                            />
                        }
                        label={<Typography sx={{ fontSize: 12, color: c.always ? 'text.disabled' : 'text.primary' }}>{c.label}</Typography>}
                        sx={{ display: 'flex', m: 0, px: 1, py: 0.25, borderRadius: '4px', '&:hover': { bgcolor: 'action.hover' } }}
                    />
                ))}
            </Popover>
        </>
    );
}

export default function DataTable({
    title,
    subtitle,
    columns,
    rows,
    rowKey,
    onRowClick,
    initialSort,
    storageKey,
    toolbar,
    emptyMessage = 'No results',
    pageSizeOptions = PAGE_SIZES,
    initialPageSize = 50,
    pagination = true,
    headerActions,
}) {
    const defaultActive = useMemo(() => columns.filter((c) => c.always || c.defaultOn).map((c) => c.id), [columns]);
    const [active, setActiveState] = useState(() => loadCols(storageKey, defaultActive));
    const [sortBy, setSortBy] = useState(initialSort?.by || null);
    const [sortDir, setSortDir] = useState(initialSort?.dir || 'desc');
    const [pageSize, setPageSize] = useState(initialPageSize);

    const setActive = useCallback((next) => {
        setActiveState(next);
        if (storageKey) {
            try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* ignore */ }
        }
    }, [storageKey]);

    const sorted = useMemo(() => {
        if (!sortBy) return rows;
        const col = columns.find((c) => c.id === sortBy);
        if (!col) return rows;
        const dir = sortDir === 'asc' ? 1 : -1;
        return [...rows].sort((a, b) => compareValues(col.accessor(a), col.accessor(b), col.type) * dir);
    }, [rows, columns, sortBy, sortDir]);

    const visible = useMemo(() => sorted.slice(0, pageSize), [sorted, pageSize]);
    const activeCols = useMemo(() => columns.filter((c) => active.includes(c.id)), [columns, active]);

    const requestSort = (id) => {
        const col = columns.find((c) => c.id === id);
        if (!col?.sortable) return;
        if (sortBy === id) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        else {
            setSortBy(id);
            setSortDir(col.type === 'number' ? 'desc' : 'asc');
        }
    };

    return (
        <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
            {/* Title row */}
            {(title || subtitle || headerActions) && (
                <Box sx={{ px: 3, pt: 2.5, pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
                    <Box sx={{ minWidth: 0 }}>
                        {title && (
                            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>
                                {title}
                            </Typography>
                        )}
                        {subtitle && (
                            <Typography sx={{ fontSize: 10, color: 'text.secondary', mt: 0.25 }}>
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                    {headerActions && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, flexWrap: 'wrap' }}>
                            {headerActions}
                        </Box>
                    )}
                </Box>
            )}

            {/* Toolbar, caller fills with filter controls; column picker
                and live row count are always present on the right edge so
                the controls cluster identically across every table. */}
            <Box sx={{ px: 3, py: 1.25, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
                    {toolbar}
                </Box>
                <Typography sx={{ fontSize: 11, color: 'text.disabled', whiteSpace: 'nowrap' }}>
                    {visible.length === sorted.length ? `${sorted.length} rows` : `${visible.length} of ${sorted.length} rows`}
                </Typography>
                <ExportMenu rows={sorted} columns={activeCols} title={title} />
                <ColumnPicker columns={columns} active={active} onChange={setActive} />
            </Box>

            {/* Table */}
            <Box sx={{ overflowX: 'auto' }}>
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <Box component="thead">
                        <Box component="tr">
                            {onRowClick && (
                                <Box
                                    component="th"
                                    sx={{
                                        fontSize: 11, fontWeight: 600, color: 'text.disabled',
                                        textTransform: 'uppercase', letterSpacing: '0.05em',
                                        px: 2, py: 1.25, textAlign: 'left',
                                        borderBottom: '1px solid', borderColor: 'divider',
                                        whiteSpace: 'nowrap', userSelect: 'none', width: '1px',
                                    }}
                                >
                                    <Box component="span" sx={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
                                        Actions
                                    </Box>
                                </Box>
                            )}
                            {activeCols.map((c) => {
                                const isSorted = sortBy === c.id;
                                return (
                                    <Box
                                        key={c.id}
                                        component="th"
                                        onClick={() => requestSort(c.id)}
                                        sx={{
                                            fontSize: 11, fontWeight: 600,
                                            color: isSorted ? 'text.primary' : 'text.disabled',
                                            textTransform: 'uppercase', letterSpacing: '0.05em',
                                            px: 2, py: 1.25, textAlign: c.align || 'left',
                                            borderBottom: '1px solid', borderColor: 'divider',
                                            whiteSpace: 'nowrap',
                                            cursor: c.sortable ? 'pointer' : 'default',
                                            userSelect: 'none',
                                            width: c.width,
                                            '&:hover': c.sortable ? { color: 'text.primary' } : undefined,
                                        }}
                                    >
                                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, justifyContent: c.align === 'right' ? 'flex-end' : 'flex-start' }}>
                                            {c.label}
                                            {c.sortable && (
                                                <Box sx={{ display: 'inline-flex', flexDirection: 'column', ml: 0.25 }}>
                                                    <ArrowDropUpIcon sx={{ fontSize: 12, m: '-3px 0', color: isSorted && sortDir === 'asc' ? 'secondary.main' : 'text.disabled' }} />
                                                    <ArrowDropDownIcon sx={{ fontSize: 12, m: '-3px 0', color: isSorted && sortDir === 'desc' ? 'secondary.main' : 'text.disabled' }} />
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                    <Box component="tbody">
                        {visible.length === 0 && (
                            <Box component="tr">
                                <Box component="td" colSpan={activeCols.length + (onRowClick ? 1 : 0)} sx={{ px: 2, py: 4, textAlign: 'center', color: 'text.disabled', fontSize: 12 }}>
                                    {emptyMessage}
                                </Box>
                            </Box>
                        )}
                        {visible.map((row, idx) => (
                            <Box
                                component="tr"
                                key={rowKey ? rowKey(row, idx) : idx}
                                sx={{
                                    borderBottom: '1px solid', borderColor: 'divider',
                                    transition: 'background 150ms',
                                    '&:hover': onRowClick ? { bgcolor: 'action.hover' } : undefined,
                                    '&:last-child': { borderBottom: 'none' },
                                }}
                            >
                                {onRowClick && (
                                    <Box component="td" sx={{ px: 2, py: 1.25, width: '1px', whiteSpace: 'nowrap' }}>
                                        <RowViewButton onClick={() => onRowClick(row)} />
                                    </Box>
                                )}
                                {activeCols.map((c) => {
                                    const align = c.align || 'left';
                                    if (c.render) {
                                        return (
                                            <Box component="td" key={c.id} sx={{ px: 2, py: 1.25, textAlign: align }}>
                                                {c.render(row)}
                                            </Box>
                                        );
                                    }
                                    const raw = c.accessor(row);
                                    const display = typeof c.format === 'function' ? c.format(raw, row) : raw ?? ', ';
                                    return (
                                        <Box component="td" key={c.id} sx={{
                                            px: 2, py: 1.25,
                                            textAlign: align,
                                            color: c.id === 'name' ? 'text.primary' : 'text.secondary',
                                            fontWeight: c.id === 'name' ? 600 : 400,
                                        }}>
                                            {display}
                                        </Box>
                                    );
                                })}
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>

            {/* Footer */}
            {pagination && (
                <Box sx={{ px: 3, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid', borderColor: 'divider', flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled', mr: 0.5 }}>
                            Per page
                        </Typography>
                        {pageSizeOptions.map((n) => {
                            const isActive = pageSize === n;
                            return (
                                <Button key={n} size="small" onClick={() => setPageSize(n)} sx={{
                                    minWidth: 0, fontSize: 11, fontWeight: 600, px: 1.25, py: '3px', borderRadius: '4px',
                                    border: '1px solid', borderColor: isActive ? 'secondary.main' : 'divider',
                                    bgcolor: isActive ? 'secondary.main' : 'transparent',
                                    color: isActive ? 'common.white' : 'text.secondary',
                                    '&:hover': { borderColor: 'secondary.main', bgcolor: isActive ? 'secondary.main' : 'transparent' },
                                }}>{n}</Button>
                            );
                        })}
                    </Box>
                    <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                        {sorted.length} total
                    </Typography>
                </Box>
            )}
        </MainCard>
    );
}
